/**
 * Read-only precedence detector for SELECT set-operator trees.
 *
 * VQE-RO renders flat UNION/INTERSECT/EXCEPT chains as a single linear
 * branch list. Multi-level parenthesized precedence trees (e.g.
 * `(A UNION B) INTERSECT (C EXCEPT D)`) cannot be expressed in a single
 * vertical branch sequence, so the renderer downgrades to
 * `'unsupported-set-precedence'`.
 *
 * The scan SKIPS a statement-leading WITH prologue first (Plan 10 §2.8 S14
 * gate lift): a recursive CTE's mandatory `UNION [ALL]` lives inside its body
 * parens (depth 1 relative to the statement), which the depth>0 scan would
 * wrongly read as a precedence tree. Set-ops inside CTE bodies are rendered by
 * the CTE cards (`extractSetOperatorBranches` over the body range), so only
 * the MAIN query body decides the downgrade. A malformed prologue falls back
 * to scanning the whole range (conservative — keeps the downgrade).
 *
 * The detection is a paren-aware character scan that respects strings,
 * comments, and dialect-specific quoting via `trySkipNonCode` (the prologue
 * walk uses the comments-only `trySkipLeadingTrivia` between tokens so quoted
 * CTE names stay visible to it). No regex is used at the structural level
 * (VQE-RO9). Per PRD §"Risks & Open Questions" R1 this lives in VQE-RO until
 * SR-RO grows a structural outline helper.
 */
import type { ParseOptions, SqlRange } from '../../../sql-grammar/model';
import type { SkipContext } from '../../../sql-resolver/classify/tokenSkip';
import {
  skipContextFor,
  trySkipLeadingTrivia,
  trySkipNonCode,
} from '../../../sql-resolver/classify/tokenSkip';

const SET_OP_TOKENS = ['UNION', 'INTERSECT', 'EXCEPT'] as const;
// `MINUS` is an Oracle-only set operator (a plain column alias elsewhere), so it
// is treated as a set-op keyword only when `engine === 'oracle'`. Kept in
// lockstep with extractSetOperatorBranches.ts / selectShape.ts.
const ORACLE_SET_OP_TOKENS = ['UNION', 'INTERSECT', 'EXCEPT', 'MINUS'] as const;

// A parenthesized group whose opening `(` is directly introduced by one of these
// keywords — or by a comparison / arithmetic operator character — is an
// EXPRESSION-context subquery (a predicate / scalar operand), not a set-op
// BRANCH or a query source. A set operator nested anywhere inside such a group
// must NOT downgrade the whole statement: the outer query is a perfectly
// renderable SELECT whose predicate merely contains a UNION. Groups introduced
// by `FROM`/`JOIN`/`,`, by a depth-0 set operator, or at the statement start
// remain BRANCH / SOURCE groups and keep their downgrade.
const EXPR_SUBQUERY_KEYWORDS: ReadonlySet<string> = new Set(['IN', 'EXISTS']);
// Quantified-comparison keywords (`> ALL (…)`, `= ANY (…)`). These are
// AMBIGUOUS: `ALL` also spells `UNION ALL` / `EXCEPT ALL` / `INTERSECT ALL`,
// where the following `(` is a genuine set-op BRANCH, not an expression. They
// therefore introduce an expression subquery ONLY when the token before them is
// a comparison operator (see `OPERATOR_CHARS`), not a set operator word.
const QUANTIFIER_KEYWORDS: ReadonlySet<string> = new Set(['ALL', 'ANY', 'SOME']);
// Comparison / arithmetic operators that introduce a scalar subquery operand,
// e.g. `total = (SELECT …)`. The immediately-preceding non-whitespace code
// character is enough (multi-char forms like `<=`, `<>`, `!=`, `||` all end in
// one of these).
const OPERATOR_CHARS: ReadonlySet<string> = new Set([
  '=', '<', '>', '!', '+', '-', '*', '/', '%', '|', '&', '^', '~',
]);

function isWordBoundary(source: string, idx: number): boolean {
  if (idx < 0 || idx >= source.length) return true;
  const ch = source[idx];
  return !(/[A-Za-z0-9_]/.test(ch));
}

function matchKeyword(source: string, i: number, end: number, kw: string): boolean {
  if (i + kw.length > end) return false;
  for (let k = 0; k < kw.length; k++) {
    const ch = source[i + k];
    if (ch !== kw[k] && ch !== kw[k].toLowerCase()) return false;
  }
  return isWordBoundary(source, i - 1) && isWordBoundary(source, i + kw.length);
}

function matchSetOperatorAt(
  source: string,
  i: number,
  end: number,
  tokens: readonly string[],
): boolean {
  for (const kw of tokens) {
    if (matchKeyword(source, i, end, kw)) return true;
  }
  return false;
}

// Advance past whitespace and comments ONLY. Comment extents come from the
// shared `trySkipLeadingTrivia` (dialect-aware: mysql `#`, `--`, `/* */`);
// strings / quoted identifiers are left in place for the caller — at a
// prologue token boundary those are real content (a quoted CTE name), not
// trivia.
function skipWsAndTrivia(source: string, from: number, end: number, ctx: SkipContext): number {
  let i = from;
  while (i < end) {
    if (/\s/.test(source[i])) {
      i++;
      continue;
    }
    const skipped = trySkipLeadingTrivia(source, i, end, ctx);
    if (skipped > i) {
      i = skipped;
      continue;
    }
    break;
  }
  return i;
}

// Consume a balanced paren group starting at `start` (which must be `(`),
// skipping strings/comments/quoted regions via `trySkipNonCode`. Returns the
// index one past the closing paren, or -1 when unbalanced within `[start, end)`.
function consumeParenGroup(source: string, start: number, end: number, ctx: SkipContext): number {
  let depth = 0;
  let i = start;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) {
      i = skipped > i ? skipped : i + 1;
      continue;
    }
    const ch = source[i];
    if (ch === '(') {
      depth++;
      i++;
      continue;
    }
    if (ch === ')') {
      if (depth > 0) depth--;
      i++;
      if (depth === 0) return i;
      continue;
    }
    i++;
  }
  return -1;
}

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;

/**
 * Skip a statement-leading `WITH [RECURSIVE] name [(cols)] AS
 * [NOT MATERIALIZED | MATERIALIZED] ( … ) [, …]` prologue, returning the
 * offset where the MAIN query body starts. When the statement doesn't begin
 * with WITH — or the prologue is malformed (missing AS / body parens,
 * unbalanced group) — returns `start` unchanged, so the caller conservatively
 * scans the whole range and any depth>0 set-op keeps the downgrade.
 */
function skipLeadingWithPrologue(
  source: string,
  start: number,
  end: number,
  ctx: SkipContext,
): number {
  let i = skipWsAndTrivia(source, start, end, ctx);
  if (!matchKeyword(source, i, end, 'WITH')) return start;
  i = skipWsAndTrivia(source, i + 'WITH'.length, end, ctx);
  if (matchKeyword(source, i, end, 'RECURSIVE')) {
    i = skipWsAndTrivia(source, i + 'RECURSIVE'.length, end, ctx);
  }
  for (;;) {
    // CTE name: a quoted identifier is consumed atomically via trySkipNonCode
    // (dialect-aware: `"…"`, backticks, mssql brackets); otherwise a bare word.
    const quoted = trySkipNonCode(source, i, end, ctx);
    if (quoted > i) {
      i = quoted;
    } else {
      if (i >= end || !IDENT_START.test(source[i])) return start;
      while (i < end && IDENT_CONT.test(source[i])) i++;
    }
    i = skipWsAndTrivia(source, i, end, ctx);
    // Optional explicit column list.
    if (i < end && source[i] === '(') {
      const closed = consumeParenGroup(source, i, end, ctx);
      if (closed < 0) return start;
      i = skipWsAndTrivia(source, closed, end, ctx);
    }
    if (!matchKeyword(source, i, end, 'AS')) return start;
    i = skipWsAndTrivia(source, i + 'AS'.length, end, ctx);
    // Optional NOT MATERIALIZED / MATERIALIZED (PG).
    if (matchKeyword(source, i, end, 'NOT')) {
      const afterNot = skipWsAndTrivia(source, i + 'NOT'.length, end, ctx);
      if (!matchKeyword(source, afterNot, end, 'MATERIALIZED')) return start;
      i = skipWsAndTrivia(source, afterNot + 'MATERIALIZED'.length, end, ctx);
    } else if (matchKeyword(source, i, end, 'MATERIALIZED')) {
      i = skipWsAndTrivia(source, i + 'MATERIALIZED'.length, end, ctx);
    }
    if (i >= end || source[i] !== '(') return start;
    const bodyClosed = consumeParenGroup(source, i, end, ctx);
    if (bodyClosed < 0) return start;
    i = skipWsAndTrivia(source, bodyClosed, end, ctx);
    if (i < end && source[i] === ',') {
      i = skipWsAndTrivia(source, i + 1, end, ctx);
      continue;
    }
    // Main query body starts here.
    return i;
  }
}

/**
 * Returns true when the statement's MAIN query body contains a set operator at
 * paren depth > 0 that belongs to a genuine parenthesized set-op BRANCH or a
 * query SOURCE (e.g. a derived table), which signals a multi-level precedence
 * tree that VQE-RO cannot flatten.
 *
 * A set operator nested inside an EXPRESSION-context subquery — `IN (…)`,
 * `EXISTS (…)`, a quantified `> ALL (…)`, or a scalar `= (…)` — does NOT
 * downgrade: the outer query is an ordinary, renderable SELECT whose predicate
 * merely contains a UNION (VQE-Polish over-reach fix). We track, per open paren
 * group, whether it is expression-introduced (see `EXPR_SUBQUERY_KEYWORDS` /
 * `OPERATOR_CHARS`); a set-op only flags when NO enclosing group is an
 * expression subquery.
 *
 * A statement-leading WITH prologue is skipped first — set-ops inside CTE bodies
 * never downgrade (see the file header).
 */
export function hasComplexSetOperatorPrecedence(
  source: string,
  range: SqlRange,
  options: ParseOptions,
): boolean {
  const ctx = skipContextFor(options);
  const tokens = options.engine === 'oracle' ? ORACLE_SET_OP_TOKENS : SET_OP_TOKENS;
  const start = range.startOffset;
  const end = Math.min(range.endOffset, source.length);
  let depth = 0;
  // Per open paren group: was it introduced as an expression subquery?
  const exprGroupStack: boolean[] = [];
  // Number of currently-open expression-subquery groups (any-ancestor test).
  let openExprGroups = 0;
  // Most recent completed bare word and last significant (non-ws) code char,
  // used to classify the group opened by the next `(`. `prevWordPrevChar` is the
  // significant char BEFORE that word began — needed to tell a quantified
  // `> ALL (` (operator before `ALL`) from a set-op `UNION ALL (` (a letter).
  let prevWord = '';
  let prevChar = '';
  let prevWordPrevChar = '';
  let curWord = '';
  let curWordPrevChar = '';
  let i = skipLeadingWithPrologue(source, start, end, ctx);
  while (i < end) {
    // Comments are trivia — skip WITHOUT disturbing the preceding-token state,
    // so `IN /* c */ (…)` still classifies the group by `IN`.
    const triviaSkipped = trySkipLeadingTrivia(source, i, end, ctx);
    if (triviaSkipped > i) {
      i = triviaSkipped;
      continue;
    }
    // A string / quoted identifier / dollar-quoted body is a real token: it
    // ends any pending word and neutralizes the preceding-token state.
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) {
      prevWord = '';
      prevChar = '';
      curWord = '';
      i = skipped > i ? skipped : i + 1;
      continue;
    }
    const ch = source[i];
    const isIdentChar = /[A-Za-z0-9_$]/.test(ch);
    // A non-identifier char completes any word that was being accumulated —
    // finalize it BEFORE we use `prevWord` to classify an opening paren.
    if (!isIdentChar && curWord !== '') {
      prevWord = curWord;
      prevWordPrevChar = curWordPrevChar;
      curWord = '';
    }
    if (ch === '(') {
      const w = prevWord.toUpperCase();
      const isExpr =
        EXPR_SUBQUERY_KEYWORDS.has(w) ||
        (QUANTIFIER_KEYWORDS.has(w) && OPERATOR_CHARS.has(prevWordPrevChar)) ||
        OPERATOR_CHARS.has(prevChar);
      exprGroupStack.push(isExpr);
      if (isExpr) openExprGroups++;
      depth++;
      prevChar = ch;
      prevWord = '';
      i++;
      continue;
    }
    if (ch === ')') {
      if (depth > 0) {
        depth--;
        if (exprGroupStack.pop()) openExprGroups--;
      }
      prevChar = ch;
      prevWord = '';
      i++;
      continue;
    }
    if (
      depth > 0 &&
      openExprGroups === 0 &&
      /[A-Za-z]/.test(ch) &&
      matchSetOperatorAt(source, i, end, tokens)
    ) {
      return true;
    }
    if (isIdentChar) {
      if (curWord === '') curWordPrevChar = prevChar; // char before this word begins
      curWord += ch;
    }
    if (!/\s/.test(ch)) prevChar = ch;
    i++;
  }
  return false;
}
